import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Pagination from '../components/Pagination';
import { getAllUsers, updateUserRole } from '../api/admin';
import { UserDto, PagedResult } from '../types/admin';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const AdminUsersPage: React.FC = () => {
  const [result, setResult] = useState<PagedResult<UserDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [filterContractor, setFilterContractor] = useState<boolean | undefined>(undefined);
  const [filterAdmin, setFilterAdmin] = useState<boolean | undefined>(undefined);
  const [pageSize, setPageSize] = useState(20);

  // Committed search state (applied on submit)
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedContractor, setAppliedContractor] = useState<boolean | undefined>(undefined);
  const [appliedAdmin, setAppliedAdmin] = useState<boolean | undefined>(undefined);
  const [appliedPageSize, setAppliedPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllUsers({
        searchTerm: appliedSearch || undefined,
        isContractor: appliedContractor,
        isAdmin: appliedAdmin,
        page,
        pageSize: appliedPageSize,
      });
      setResult(data);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, appliedContractor, appliedAdmin, appliedPageSize]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [fetchUsers, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput);
    setAppliedContractor(filterContractor);
    setAppliedAdmin(filterAdmin);
    setAppliedPageSize(pageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateRole = async (userId: string, isAdmin?: boolean, isContractor?: boolean) => {
    try {
      await updateUserRole(userId, isAdmin, isContractor);
      await fetchUsers(currentPage);
    } catch {
      setError('Failed to update role.');
    }
  };

  const users = result?.items ?? [];

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-primary hover:underline text-sm">Admin</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Users</h1>
        </div>

        {/* Search and filter bar */}
        <form onSubmit={handleSearch} className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Email or name"
              className="w-full px-3 py-2 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Role</label>
            <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
              <input
                type="checkbox"
                checked={filterContractor === true}
                onChange={e => setFilterContractor(e.target.checked ? true : undefined)}
              />
              Contractors only
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
              <input
                type="checkbox"
                checked={filterAdmin === true}
                onChange={e => setFilterAdmin(e.target.checked ? true : undefined)}
              />
              Admins only
            </label>
          </div>

          <div>
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Per page</label>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="px-3 py-2 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <button
            type="submit"
            className="px-5 py-2 bg-primary text-on-primary rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Search
          </button>
        </form>

        {error && <p className="text-error mb-4">{error}</p>}

        {result && (
          <p className="text-sm text-on-surface-variant mb-3">
            {result.totalCount} user{result.totalCount !== 1 ? 's' : ''} found
          </p>
        )}

        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">Loading...</div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">No users found.</div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-surface-container">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Email</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Name</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Contractor</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-surface-container-lowest">
                    <td className="py-3 px-4 text-sm text-on-surface">{user.email}</td>
                    <td className="py-3 px-4 text-sm text-on-surface">{user.firstName} {user.lastName}</td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={user.isContractor}
                        onChange={(e) => handleUpdateRole(user.id, undefined, e.target.checked)}
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={user.isAdmin}
                        onChange={(e) => handleUpdateRole(user.id, e.target.checked, undefined)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {result && result.totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={result.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminUsersPage;
